/**
 * Shared panel header for simulation modules.
 *
 * Renders a consistent header row with an optional eyebrow/title and a
 * right-aligned slot for actions (e.g. overview buttons, toolbars). Matches
 * the visual shape historically used by `QuestionPanel`, `TextPanel`, and
 * the Writing panels so the three simulation modules align exactly.
 *
 * The header is purely presentational — it owns no state, no side effects,
 * and reads no context beyond what is passed via props.
 *
 * Surface tokens: typically placed over `bg-card/50` or `bg-muted/20` in
 * module panels; the parent controls the surrounding `bg-exam-*` token.
 */

import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface SimulationPanelHeaderProps {
  /** Small label above the title (uppercase tracking-wider); optional. */
  readonly eyebrow?: ReactNode;
  /** Icon placed before the title; optional. */
  readonly icon?: ReactNode;
  /** Primary title text; optional. */
  readonly title?: ReactNode;
  /**
   * Optional inline meta rendered after the title (e.g. question range
   * "Q1–Q4"). On mobile it stacks below the title; on desktop it sits
   * inline on the title's baseline, matching the current QuestionPanel.
   */
  readonly titleSuffix?: ReactNode;
  /** Optional right-aligned content (buttons, pills, toolbars). */
  readonly rightSlot?: ReactNode;
  /** When true, title + eyebrow are horizontally centered. */
  readonly centered?: boolean;
  /**
   * When true, the title is allowed to wrap onto multiple lines instead of
   * being truncated with an ellipsis. Useful for panels whose `title`
   * carries a long task instruction (e.g. Writing prompt panel).
   */
  readonly wrapTitle?: boolean;
  /** Extra classes appended to the root element. */
  readonly className?: string;
}

export function SimulationPanelHeader({
  eyebrow,
  icon,
  title,
  titleSuffix,
  rightSlot,
  centered = false,
  wrapTitle = false,
  className,
}: SimulationPanelHeaderProps) {
  const hasLeftContent = Boolean(icon || eyebrow || title || titleSuffix);

  return (
    <div
      className={cn(
        'px-4 sm:px-6 py-3 sm:py-4 border-b border-border bg-card/50 shrink-0',
        className,
      )}
    >
      <div
        className={cn(
          'flex gap-3',
          wrapTitle ? 'items-start' : 'items-center',
          centered ? 'justify-center text-center' : 'justify-between',
        )}
      >
        {hasLeftContent && (
          <div
            className={cn(
              'flex gap-2 min-w-0',
              wrapTitle ? 'items-start' : 'items-center',
              centered && 'justify-center',
            )}
          >
            {icon ? <span className="shrink-0 text-primary">{icon}</span> : null}
            <div
              className={cn(
                'flex flex-col sm:flex-row sm:gap-2',
                wrapTitle ? 'sm:items-start' : 'sm:items-baseline truncate',
                centered && 'items-center sm:items-baseline',
              )}
            >
              {eyebrow !== undefined && eyebrow !== null && (
                <span
                  className={cn(
                    'font-ui text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap',
                    wrapTitle && 'sm:mt-0.5',
                  )}
                >
                  {eyebrow}
                </span>
              )}
              {title !== undefined && title !== null && (
                <h2
                  className={cn(
                    'font-ui font-bold text-foreground text-sm sm:text-lg',
                    wrapTitle ? 'whitespace-pre-line break-words' : 'truncate',
                  )}
                >
                  {title}
                </h2>
              )}
              {titleSuffix !== undefined && titleSuffix !== null && (
                <span className="font-ui text-[10px] sm:text-sm text-muted-foreground whitespace-nowrap">
                  {titleSuffix}
                </span>
              )}
            </div>
          </div>
        )}

        {rightSlot ? (
          <div className="flex items-center gap-2 shrink-0">{rightSlot}</div>
        ) : null}
      </div>
    </div>
  );
}
